import type { IncomeSummary, UpsertOwnerShareInput } from '@sea-snag/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { incomeApi } from '../lib/api-client';
import { queryKeys } from '../lib/query-keys';

export function useIncome(orgPid: string, year: number, boatPid: string) {
  return useQuery({
    queryKey: queryKeys.analytics.income(orgPid, String(year), boatPid),
    queryFn: () => incomeApi.get({ year: String(year), boatPid }),
    enabled: !!orgPid,
  });
}

/** Changing the rate re-computes every year, so the whole income cache is dropped. */
export function useSetTaxRate(orgPid: string) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (taxRatePct: number) => incomeApi.setTaxRate(taxRatePct),
    onSuccess: (result) => {
      toast.success(`Tax rate set to ${result.taxRatePct}%`);
      void qc.invalidateQueries({ queryKey: queryKeys.analytics.incomeAll(orgPid) });
    },
    onError: () => toast.error('Failed to update the tax rate'),
  });
}

/**
 * Optimistically updates the share % (and the derived owner/management income)
 * for one boat across every currency block, then reconciles on success.
 */
export function useUpdateOwnerShare(orgPid: string, year: number, boatPid: string) {
  const qc = useQueryClient();
  const queryKey = queryKeys.analytics.income(orgPid, String(year), boatPid);

  return useMutation({
    mutationFn: (args: { boatPid: string; data: UpsertOwnerShareInput }) =>
      incomeApi.updateOwnerShare(args.boatPid, args.data),
    onMutate: async (args) => {
      await qc.cancelQueries({ queryKey });
      const previous = qc.getQueryData<IncomeSummary>(queryKey);
      if (previous) {
        qc.setQueryData<IncomeSummary>(
          queryKey,
          applyShare(previous, args.boatPid, args.data.sharePct)
        );
      }
      return { previous };
    },
    onError: (_err, _args, ctx) => {
      if (ctx?.previous) qc.setQueryData(queryKey, ctx.previous);
      toast.error('Failed to update owner share');
    },
    onSuccess: () => {
      toast.success('Owner share updated');
      void qc.invalidateQueries({ queryKey: queryKeys.analytics.incomeAll(orgPid) });
    },
  });
}

function applyShare(summary: IncomeSummary, boatPid: string, sharePct: number): IncomeSummary {
  const r2 = (n: number) => Math.round(n * 100) / 100;
  return {
    ...summary,
    byCurrency: summary.byCurrency.map((cur) => {
      let ownerTotal = 0;
      let managementTotal = 0;
      const perBoat = cur.perBoat.map((b) => {
        const pct = b.boatPid === boatPid ? sharePct : b.sharePct;
        const ownerIncome = pct != null ? r2(b.net * (pct / 100)) : null;
        const managementIncome = ownerIncome != null ? r2(b.net - ownerIncome) : null;
        if (ownerIncome != null) ownerTotal += ownerIncome;
        if (managementIncome != null) managementTotal += managementIncome;
        return { ...b, sharePct: pct, ownerIncome, managementIncome };
      });
      return {
        ...cur,
        perBoat,
        charter: {
          ...cur.charter,
          ownerTotal: r2(ownerTotal),
          managementTotal: r2(managementTotal),
        },
        boatsMissingShare: perBoat.filter((b) => b.sharePct == null).length,
      };
    }),
  };
}
